import React, { useEffect, useState } from 'react';
import {
    Card,
    AdvancedTable,
    AdvancedTableHeader,
    NotificationBundle,
    WarningModal,
    AnimatedTabs,
} from 'components';
import { Dispatch, IRootState } from '../../redux/store/configureStore';
import { push } from 'connected-react-router';
import { Tab } from 'react-bootstrap';
import { connect } from 'react-redux';
import { ExamApplication } from '../../api/ApiClient';
import { ExamApplicationDetailsContainer } from '..';
import { useLocalErrorHandler } from 'hooks';
import { IFetchExamApplicationsProps } from '../../redux/models/examApplications';
import attestationIcon from 'components/assets/svg/attestation_icon.svg';

const ExamApplicationsListContainer = ({
    setErrorHandler,
    events,
    fetchExamApplications,
    fetchExamApplicationsPaymentRequired,
    examApplicationsList,
    clearExamApplications,
    onPush,
    apiError,
    getDraftsList,
    draftsList,
}: Props) => {
    useLocalErrorHandler(setErrorHandler);

    const initLocalEvents: { [key: string]: number } = {};
    const params = new URLSearchParams(window.location.search);
    const param = params.get('tab');
    const newTab = param && (param === TabType.Draft || param === TabType.Payment || param === TabType.Review) ? param : null;

    const [tab, setTab] = React.useState(newTab || TabType.Review);
    const [sortOrder, setSortOrder] = useState(true);
    const [localEvents, setLocalEvents] = useState(initLocalEvents);
    const [selectedExamApplication, setSelectedExamApplication] = useState<ExamApplication | null | undefined>(null);

    useEffect(() => {
        setLocalEvents(events);
    }, [events]);

    useEffect(() => {
        getDraftsList({});
    }, []);

    const getExamMethodMap = (requestProps: IFetchExamApplicationsProps) => {
        if (tab === TabType.Review) {
            return fetchExamApplications(requestProps);
        }
        return fetchExamApplicationsPaymentRequired(requestProps);
    };

    useEffect(() => { getExamMethodMap({}); }, [tab]);

    const createTableData = examApplicationsList && examApplicationsList.items.map(({
        examApplicationId, applicantFullName, applicantPhone, applicantEmail, applicationSubmitDate,
    }) => ({
        id: examApplicationId,
        columns: [
            applicantFullName,
            applicantPhone,
            applicantEmail,
            applicationSubmitDate.format('DD.MM.YYYY в HH:mm'),
        ],
    }));

    const createDraftTableData = draftsList && draftsList.items.map(({
        examApplicationId, applicantFullName, applicantPhone, applicationSubmitDate,
    }) => ({
        id: examApplicationId,
        columns: [
            applicantFullName,
            applicantPhone,
            applicationSubmitDate.format('DD.MM.YYYY в HH:mm'),
        ],
    }));

    const applicationsHeader: AdvancedTableHeader[] = [
        { fieldName: 'applicantFullName', title: 'ФАМИЛИЯ, ИМЯ, ОТЧЕСТВО' },
        { fieldName: 'applicantPhone', title: 'Телефон' },
        { fieldName: 'applicantEmail', title: 'Почта' },
        {
            fieldName: 'applicationSubmitDate',
            title: 'Дата и время поступления',
        },
    ];

    const applicationsDraftHeader: AdvancedTableHeader[] = [
        { fieldName: 'applicantFullName', title: 'ФАМИЛИЯ, ИМЯ, ОТЧЕСТВО' },
        { fieldName: 'applicantPhone', title: 'Телефон' },
        { fieldName: 'applicationSubmitDate', title: 'Дата и время создания' },
    ];

    const handleExpertSelect = (examApplicationId: string) => setSelectedExamApplication(examApplicationsList ? examApplicationsList.items.find(e => e.examApplicationId === examApplicationId) : null);
    const handleDraftSelect = (examApplicationId: string) => onPush(`/exam-draft/${examApplicationId}`);
    const handleSearch = (fieldName: string, value: string) => getExamMethodMap({ searchField: fieldName, searchTerm: value });
    const handleSearchDraft = (fieldName: string, value: string) => getDraftsList({ searchField: fieldName, searchTerm: value });
    const handleOrder = (fieldName: string) => {
        setSortOrder(!sortOrder);
        getExamMethodMap({ sortField: fieldName, sortOrder });
    };
    const handleOrderDraft = (fieldName: string) => {
        setSortOrder(!sortOrder);
        getDraftsList({ sortField: fieldName, sortOrder });
    };

    const handleSelectTab = (tabName: string) => {
        clearExamApplications();
        if (tabName === TabType.Draft || tabName === TabType.Payment || tabName === TabType.Review) {
            setTab(tabName);
            onPush(`/exam-applications?tab=${tabName}`);
        }
    };

    const bundleWrapper = (element: React.ReactNode, eventCount: number) => (
        <div className="d-flex align-items-center">
            <div>{element}</div>
            <NotificationBundle className="ml-3" dark value={eventCount} />
        </div>
    );

    return (
        <Card
          title="Заявки на запись"
          category="Заявки на запись от соискателей"
          apiError={apiError}
          withoutPadding
        >
            <AnimatedTabs
              id="ExamApplicationsListContainerTabs"
              activeKey={tab}
              onSelect={handleSelectTab}
            >
                <Tab
                  eventKey={TabType.Review}
                  title={localEvents.ExamApplications
                        ? bundleWrapper('На обработку', localEvents.ExamApplications)
                        : 'На обработку'}
                >
                    <Card
                      title="Очередь на обработку"
                      category="Заявки от соискателей, которые требуют обработки ЦОК"
                      withoutPadding
                      withoutBorder
                    >
                        <AdvancedTable
                          data={createTableData || []}
                          tableHeaders={applicationsHeader}
                          handlePageChange={fetchExamApplications}
                          handleSearch={handleSearch}
                          handleOrder={handleOrder}
                          handleRowClick={handleExpertSelect}
                          page={(examApplicationsList && examApplicationsList.page) || 1}
                          pageSize={(examApplicationsList && examApplicationsList.pageSize) || 10}
                          total={(examApplicationsList && examApplicationsList.total) || 1}
                          placeholderIcon={attestationIcon}
                          placeholderTitle="Нет поданных заявок"
                          placeholderText="Здесь будут отображаться все заявки от соискателей, которые требуют обработки "
                        />
                    </Card>
                </Tab>
                <Tab
                  eventKey={TabType.Payment}
                  title={localEvents.ExamApplicationsPaymentRequired
                        ? bundleWrapper('Ожидают оплаты', localEvents.ExamApplicationsPaymentRequired)
                        : 'Ожидают оплаты'}
                >
                    <Card
                      title="Заявки на оплату"
                      category="Заявки от соискателей, ожидающие оплаты"
                      withoutPadding
                      withoutBorder
                    >
                        <AdvancedTable
                          data={createTableData || []}
                          tableHeaders={applicationsHeader}
                          handlePageChange={fetchExamApplications}
                          handleSearch={handleSearch}
                          handleOrder={handleOrder}
                          handleRowClick={handleExpertSelect}
                          page={(examApplicationsList && examApplicationsList.page) || 1}
                          pageSize={(examApplicationsList && examApplicationsList.pageSize) || 10}
                          total={(examApplicationsList && examApplicationsList.total) || 1}
                          placeholderIcon={attestationIcon}
                          placeholderTitle="Нет одобренных заявок"
                          placeholderText="Здесь будут отображаться все заявки от соискателей, которые требуют оплаты "
                        />
                    </Card>
                </Tab>
                <Tab eventKey={TabType.Draft} title="Черновики">
                    <Card
                      title="Черновики заявок"
                      category="Заявки на экзамен в процессе заполнения"
                      withoutPadding
                      withoutBorder
                    >
                        <AdvancedTable
                          data={createDraftTableData || []}
                          tableHeaders={applicationsDraftHeader}
                          handlePageChange={getDraftsList}
                          handleSearch={handleSearchDraft}
                          handleOrder={handleOrderDraft}
                          handleRowClick={handleDraftSelect}
                          page={(draftsList && draftsList.page) || 1}
                          pageSize={(draftsList && draftsList.pageSize) || 10}
                          total={(draftsList && draftsList.total) || 1}
                          placeholderIcon={attestationIcon}
                          placeholderTitle="Черновики отсутствуют"
                          placeholderText="Здесь будут отображаться все черновики заявок на экзамен"
                        />
                    </Card>
                </Tab>
            </AnimatedTabs>

            {selectedExamApplication ? (
                <WarningModal
                  title={selectedExamApplication.applicantFullName}
                  show={!!selectedExamApplication}
                  onHide={() => setSelectedExamApplication(null)}
                  footerClassNames="p-0"
                  bodyClassNames="p-0"
                  withoutTitlePadding
                  withoutTitleMargin
                  withoutCancelButton
                  large
                >
                    <ExamApplicationDetailsContainer
                      applicationId={selectedExamApplication.examApplicationId}
                    />
                </WarningModal>
            ) : null}
        </Card>
    );
};

const mapState = (state: IRootState) => ({
    examApplicationsList: state.examApplications.examApplicationsList,
    events: state.eventsState,
    apiError: state.errors.error,
    draftsList: state.examApplications.draftsList,
});

const mapDispatch = (dispatch: Dispatch) => ({
    onPush: (key: string) => dispatch(push(key)),
    fetchExamApplications: dispatch.examApplications.fetchExamApplicationsAsync,
    fetchExamApplicationsPaymentRequired: dispatch.examApplications.fetchExamApplicationsPaymentRequiredAsync,
    clearExamApplications: dispatch.examApplications.clearExamApplications,
    setErrorHandler: dispatch.errors.setErrorHandler,
    getDraftsList: dispatch.examApplications.getDraftsListAsync,
});

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;
type Props = connectedProps;

export default connect(mapState, mapDispatch)(ExamApplicationsListContainer);

enum TabType {
    Review = 'to-review',
    Payment = 'waiting-for-payment',
    Draft = 'draft'
}
